// Dependencies
import { FC } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Autorenew } from '@mui/icons-material';

// Components
import { Dialog } from '../../Dialog';

// Styles
import styles from './styles';

interface ForceGenerateConfirmDialogProps {
  open: boolean;
  todayLabel: string;
  nextScheduledLabel?: string;
  isGenerating?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ForceGenerateConfirmDialog: FC<ForceGenerateConfirmDialogProps> = ({
  open,
  todayLabel,
  nextScheduledLabel,
  isGenerating = false,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={isGenerating ? () => undefined : onClose} sx={styles.dialog}>
      <Typography mb={16} variant="headline">
        Force Generate PDF
      </Typography>
      <Typography mb={20} variant="body2" color="gray.dark">
        Generate and publish the factsheet now instead of waiting for the next scheduled run.
      </Typography>

      <Box component="ul" sx={styles.list}>
        <li>
          A new PDF will be created using the <strong>latest available Axion data</strong> (not
          necessarily today&apos;s calendar date) and your latest saved configuration (any unsaved
          edits will be saved first).
        </li>
        <li>
          The document&apos;s <strong>current PDF link will be updated immediately</strong> with this
          new file — existing links will serve the updated PDF.
        </li>
        {nextScheduledLabel ? (
          <li>
            You do not need to wait until the next scheduled publication
            ({nextScheduledLabel}).
          </li>
        ) : null}
        <li>
          <strong>Scheduled automation is not changed</strong> — the cron job will continue to run on
          its regular schedule.
        </li>
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={12} mt={32}>
        <Button variant="text" onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          variant="varied-contained"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <Autorenew />}
          onClick={onConfirm}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating…' : 'Force Generate'}
        </Button>
      </Stack>
    </Dialog>
  );
};
